import * as React from "react";
import { useEffect, useState } from "react";
import { deleteTicket, TicketDatabase } from "./Utils/TicketDatabase";
import TicketAppBar from "./FlightPackage/TicketAppBar";
import { SyncStatus } from "./Utils/Interfaces";
import TicketPage from "./FlightPackage/TicketPage";
import { GlobalContext, GlobalContextValue } from "./Utils/Contexts";
import { SHOW_MENU } from "./Utils/TicketValidator";
import { PersonExtended, TicketExtended, TicketLocationExtended } from "./Utils/TypeExtensions";
import TicketLoadingDialogue from "./Utils/TicketLoadingDialogue";
import SyncManager from "./Sync/SyncManager";
import DeleteTicketConfirmationDialog from "./Components/DeleteTicketConfirmationDialog";
import SignaturePendingDialog from "./Components/SignaturePendingDIalog";
import { IsDemoMode } from "./sharedCode/HelperMethods";

import { APPLICATION_VERSION } from "./sharedCode/metadata";
import { ObjectUtils } from "./Utils/ObjectUtils";
import ProxyPilotDialog from "./Components/ProxyPilotDialogue";
import AddTicketDialog from "./Components/AddTicketDialog";
import { PDFViewer } from "@react-pdf/renderer";
import { PdfFontLoader } from "./Utils/PdfFontLoader";
import { IDENTITY_BREADCRUMB } from "./App";
import * as Sentry from "@sentry/react";
import ForceReloadDialog from "./Components/ForceReloadDialog";
import { useLiveQuery } from "dexie-react-hooks";
import Box from "@mui/material/Box";
import { CircularProgress } from "@mui/material";
import { UserPreferences } from "./routes/flightPackage/userPreferences";
import { ReleaseNotes } from "./routes/releaseNotes";

interface AppContainerProps {
    setAuthorizationToken(newToken: string): void;

    toggleDemoMode(): void;
}

export const INITIALIZATION_TICKET_ID = -99999;

interface LookupChanges {
    removed: TicketLocationExtended[];
    added: TicketLocationExtended[];
    modified: TicketLocationExtended[];
}

export default function AppContainer(props: AppContainerProps): JSX.Element {
    function initializeContext() {
        return {
            syncStatus: SyncStatus.Loading,
            isDeletingTicket: false,
            isConfirmingSignaturePending: false,
            openPage: SHOW_MENU,
            viewMonth: "",
            selectedTicketId: INITIALIZATION_TICKET_ID,
            setSelectedTicketId(ticketId: number) {
                handleSetSelectedTicketId(ticketId);
            },
            setShowLoadingTicketsDialogue(shouldShow: boolean) {
                handleSetShowLoadingTicketsDialogue(shouldShow);
            },
            setShowProxyPilotDialogue(shouldShow: boolean, pilotToProxy?: PersonExtended | undefined) {
                handleSetShowProxyPilotDialogue(shouldShow, pilotToProxy);
            },
            setShowAddTicketDialogue(shouldShow: boolean, ticketToClone?: TicketExtended | undefined) {
                handleSetShowAddTicketDialogue(shouldShow, ticketToClone);
            },
            showUserPreferences(shouldShow: boolean) {
                handleShowUserPreferences(shouldShow);
            },
            toggleDemoMode(context: GlobalContextValue, isSuperUser: boolean) {
                handleToggleDemoMode(context, isSuperUser);
            },
            setLoggedInUser(user: PersonExtended) {
                handleSetLoggedInUser(user);
            },
            deleteSelectedTicket() {
                handleDeleteSelectedTicket();
            },
            displaySignaturePendingDialogue() {
                handleDisplaySignaturePendingDialogue();
            },
            cloneTicket(ticketToClone: TicketExtended) {
                handleCloneTicket(ticketToClone);
            },
            setOpenPage(page: string) {
                handleSetOpenPage(page);
            },
            setViewMonth(viewMonth: string) {
                handleSetViewMonth(viewMonth);
            },
            setShouldPushTickets(shouldPush: boolean) {
                handleSetShouldPushTickets(shouldPush);
            },
            setShouldSync(shouldSync: boolean) {
                handleSetShouldSync(shouldSync);
            },
            showForceReloadDialogue() {
                handleShowForceReloadDialogue();
            },
            showReleaseNotes(shouldShow: boolean) {
                handleShowReleaseNotes(shouldShow);
            },
        } as GlobalContextValue;
    }
    const [globalContextValue, setGlobalContextValue] = React.useState<GlobalContextValue>(initializeContext);

    const [showForceReloadDialogue, setShowForceReloadDialogue] = useState(false);

    const [showTicketLoadingDialogue, setShowTicketLoadingDialogue] = useState(false);
    const [showAddTicketDialogue, setShowAddTicketDialogue] = useState(false);
    const [ticketToClone, setTicketToClone] = useState<TicketExtended | undefined>(undefined);
    const [showProxyPilotDialogue, setShowProxyPilotDialogue] = useState(false);
    const [loadingPage, setLoadingPage] = useState(true);
    const [showUserPreferences, setShowUserPreferences] = useState(false);
    const [showReleaseNotes, setShowReleaseNotes] = useState(false);

    // const aircraftList = useLiveQuery(async () => TicketDatabase.aircraft.toArray());
    // const ameList = useLiveQuery(async () => TicketDatabase.ame.toArray());
    // const baseLocationList = useLiveQuery(async () => TicketDatabase.companyBase.toArray());
    // const companyList = useLiveQuery(async () => TicketDatabase.company.toArray());
    // const jobList = useLiveQuery(async () => TicketDatabase.job.toArray());
    // const lineItemTypeList = useLiveQuery(async () => TicketDatabase.ticketLineItemType.toArray());
    // const localSettingList = useLiveQuery(async () => TicketDatabase.localSetting.toArray());
    // const minimumsRequiredList = MINIMUMS_VISIBLE_COMPANIES;
    // const pilotList = useLiveQuery(async () => TicketDatabase.pilot.toArray());
    // const ticketList = useLiveQuery(async () => TicketDatabase.ticket.toArray());
    const userListOfOne = useLiveQuery(async () => TicketDatabase.user.toArray());

    const handleSetSyncStatus = (syncStatus: SyncStatus) => {
        setGlobalContextValue((prev) => {
            return { ...prev, syncStatus };
        });
    };

    function handleSetShowLoadingTicketsDialogue(shouldShow: boolean) {
        setShowTicketLoadingDialogue(shouldShow);
    }

    function handleSetShowProxyPilotDialogue(shouldShow: boolean, pilotToProxy: PersonExtended | undefined = undefined) {
        setShowProxyPilotDialogue(shouldShow);
        setGlobalContextValue((prev) => {
            // setShowTicketLoadingDialogue(true);
            return { ...prev, proxyUser: pilotToProxy };
        });
    }

    function handleSetShowAddTicketDialogue(shouldShow: boolean, ticketToClone: TicketExtended | undefined = undefined) {
        setTicketToClone(ticketToClone);
        setShowAddTicketDialogue(shouldShow);
    }

    function handleShowUserPreferences(shouldShow: boolean) {
        setShowUserPreferences(shouldShow);
    }

    function handleShowReleaseNotes(shouldShow: boolean) {
        setShowReleaseNotes(shouldShow);
    }

    function handleToggleDemoMode(context: GlobalContextValue, isSuperUser: boolean) {
        if (isSuperUser) {
            if (!IsDemoMode()) {
                setShowProxyPilotDialogue(true);
            }
        }

        props.toggleDemoMode();
    }

    const handleSetLoggedInUser = (user: PersonExtended) => {
        setGlobalContextValue((prev) => {
            return { ...prev, loggedInUser: user };
        });
    };

    function handleSetShouldPushTickets(shouldPush: boolean) {
        setGlobalContextValue((prev) => {
            return { ...prev, shouldPushTickets: shouldPush };
        });
    }

    function handleSetShouldSync(shouldSync: boolean) {
        setGlobalContextValue((prev) => {
            return { ...prev, shouldSync: shouldSync };
        });
    }

    function handleShowForceReloadDialogue() {
        setShowForceReloadDialogue(true);
    }

    function handleSetOpenPage(pageTitle: string) {
        setGlobalContextValue((prev) => {
            return { ...prev, openPage: pageTitle };
        });
    }

    function handleSetViewMonth(viewMonth: string) {
        setGlobalContextValue((prev) => {
            return { ...prev, viewMonth };
        });
    }

    function handleSetSelectedTicketId(ticketId: number) {
        setGlobalContextValue((prev) => {
            return {
                ...prev,
                selectedTicketId: ticketId,
                openPage: SHOW_MENU,
            } as GlobalContextValue;
        });
    }

    const handleCloneTicket = (ticketToClone: TicketExtended) => {
        handleSetShowAddTicketDialogue(true, ticketToClone);
    };

    const validTicketIsSelected = (context: GlobalContextValue): boolean => {
        return context.selectedTicketId !== null && context.selectedTicketId !== undefined && context.selectedTicketId !== INITIALIZATION_TICKET_ID;
    };

    const handleDeleteSelectedTicket = () => {
        setGlobalContextValue((prev) => {
            return {
                ...prev,
                isDeletingTicket: validTicketIsSelected(prev),
            } as GlobalContextValue;
        });
    };

    const handleTicketDeleteConfirmation = async (doDelete: boolean) => {
        if (doDelete) {
            const newSelectedId = await deleteTicket(globalContextValue.selectedTicketId);

            setGlobalContextValue((prev) => {
                return {
                    ...prev,
                    selectedTicketId: newSelectedId,
                    isDeletingTicket: false,
                } as GlobalContextValue;
            });

            return;
        }

        setGlobalContextValue((prev) => {
            return {
                ...prev,
                isDeletingTicket: false,
            } as GlobalContextValue;
        });
    };

    const handleDisplaySignaturePendingDialogue = () => {
        setGlobalContextValue((prev) => {
            return {
                ...prev,
                isConfirmingSignaturePending: true,
            } as GlobalContextValue;
        });
    };

    const handleSignaturePendingConfirmation = (isConfirmed: boolean) => {
        setGlobalContextValue((prev) => {
            return {
                ...prev,
                isConfirmingSignaturePending: false,
                shouldPushTickets: isConfirmed,
            } as GlobalContextValue;
        });
    };

    useEffect(() => {
        if (userListOfOne) {
            setGlobalContextValue((prev) => {
                return {
                    ...prev,
                    loggedInUser: ObjectUtils.convertToUppercase(userListOfOne && userListOfOne.length === 1 ? userListOfOne[0] : ({} as PersonExtended)),
                } as GlobalContextValue;
            });
        }
    }, [userListOfOne]);

    // The app will attempt to do a sync any time it is installed or reloaded, so we use that sync completion as a trigger for release notes and loading tickets
    useEffect(() => {
        const firstRunAfterInstall = localStorage.getItem("TICKET_APP_INITIALIZED") !== "Y";
        if (globalContextValue.syncStatus === SyncStatus.Fresh) {
            if (firstRunAfterInstall) {
                localStorage.setItem("TICKET_APP_INITIALIZED", "Y");
                localStorage.setItem("INSTALLED_VERSION", APPLICATION_VERSION);
                handleSetShowLoadingTicketsDialogue(true);
            } else {
                const previousRelease = localStorage.getItem("INSTALLED_VERSION");
                if (previousRelease !== APPLICATION_VERSION) {
                    localStorage.setItem("INSTALLED_VERSION", APPLICATION_VERSION);
                    handleShowReleaseNotes(true);
                }
            }
        }
    }, [globalContextValue.syncStatus]);

    useEffect(() => {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const fromHomeScreen = window.matchMedia("(display-mode: standalone)").matches;
        const breadcrumbString = `app: ${APPLICATION_VERSION} home/safari: ${fromHomeScreen}/${isSafari} | user: ${globalContextValue?.loggedInUser?.selectName} | userAgent: ${navigator.userAgent}`;
        localStorage.setItem(IDENTITY_BREADCRUMB, breadcrumbString); // TODO why don't I just put this in breadcrumbJSON?

        const breadcrumbJSON = {
            isSafari,
            fromHomeScreen,
            user_id: globalContextValue?.loggedInUser?.id ?? undefined,
            username: globalContextValue?.loggedInUser?.selectName ?? undefined,
            email: globalContextValue?.loggedInUser?.email_work ?? undefined,
        };
        Sentry.setContext("app_info", breadcrumbJSON);
    }, [globalContextValue.loggedInUser, navigator.userAgent]);

    if (!userListOfOne) {
        return (
            <Box>
                <CircularProgress size={50} sx={{ margin: 10 }} />
            </Box>
        );
    }

    return (
        <>
            <GlobalContext.Provider value={globalContextValue}>
                <PDFViewer style={{ display: "none" }} showToolbar={false}>
                    <PdfFontLoader />
                </PDFViewer>
                <TicketAppBar setAuthorizationToken={props.setAuthorizationToken} showForceReloadDialogue={handleShowForceReloadDialogue} />
                {showTicketLoadingDialogue && <TicketLoadingDialogue />}
                {showForceReloadDialogue && <ForceReloadDialog />}
                {showProxyPilotDialogue && <ProxyPilotDialog />}
                {showAddTicketDialogue && <AddTicketDialog ticketToClone={ticketToClone} showForceReloadDialogue={handleShowForceReloadDialogue} />}
                <SyncManager setStatus={handleSetSyncStatus}></SyncManager>
                {globalContextValue.isDeletingTicket && <DeleteTicketConfirmationDialog setDeleteConfirmation={handleTicketDeleteConfirmation} />}
                {globalContextValue.isConfirmingSignaturePending && (
                    <SignaturePendingDialog showForceReloadDialogue={handleShowForceReloadDialogue} confirmSignaturePending={handleSignaturePendingConfirmation} />
                )}
                {showUserPreferences && <UserPreferences />}
                {showReleaseNotes && <ReleaseNotes />}
                <TicketPage />
            </GlobalContext.Provider>
        </>
    );
}
